import { type FormEvent, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { MemberAvatar } from '@/components/clubs'
import { BookThumb } from '@/components/insights'
import { Button } from '@/components/ui'
import { useAuth } from '@/contexts/auth'
import { currentStop, nextStop, usePassTravelingBook } from '@/hooks/traveling'
import { cn } from '@/lib/utils'
import { daysBetween, parseDate } from '@/lib/utils/date'
import type { ClubMember, TravelingBook } from '@/types'

interface TripCardProps {
  trip: TravelingBook
  members: Map<string, ClubMember>
}

/** A traveling book: route with where it is now, readers' notes, and "pass it on" */
export function TripCard({ trip, members }: TripCardProps) {
  const { t, i18n } = useTranslation()
  const { user } = useAuth()
  const pass = usePassTravelingBook()
  const [passing, setPassing] = useState(false)
  const [note, setNote] = useState('')

  const current = currentStop(trip)
  const next = nextStop(trip)
  const owner = members.get(trip.owner_id)
  const name = (userId: string | null) =>
    (userId && members.get(userId)?.display_name) || t('challenges.formerMember')
  const canPass =
    trip.status === 'traveling' && (user?.id === trip.owner_id || user?.id === current?.user_id)
  const daysWithReader = current?.received_on
    ? daysBetween(parseDate(current.received_on), new Date())
    : 0
  const notes = trip.stops.filter((s) => s.note)
  const shortDate = (d: string) =>
    new Intl.DateTimeFormat(i18n.language, { day: 'numeric', month: 'short' }).format(parseDate(d))

  const submit = async (e: FormEvent) => {
    e.preventDefault()
    await pass.mutateAsync({ trip, note })
    setPassing(false)
    setNote('')
  }

  // Route: owner → readers → owner
  const route = [
    { key: 'start', userId: trip.owner_id, state: 'done' as const },
    ...trip.stops.map((s) => ({
      key: s.id,
      userId: s.user_id,
      state: s.passed_on
        ? ('done' as const)
        : s.received_on
          ? ('current' as const)
          : ('next' as const),
    })),
    {
      key: 'end',
      userId: trip.owner_id,
      state: trip.status === 'home' ? ('current' as const) : ('next' as const),
    },
  ]

  return (
    <li className="space-y-3 rounded-lg border border-border p-4">
      <div className="flex items-start gap-3">
        <BookThumb src={trip.cover_url} title={trip.title} />
        <div className="min-w-0 flex-1">
          <p className="truncate font-medium text-foreground">{trip.title}</p>
          {trip.author && <p className="truncate text-xs text-muted-foreground">{trip.author}</p>}
          <p className="mt-1 text-xs text-muted-foreground">
            {t('traveling.ownedBy', { name: owner?.display_name ?? '' })}
          </p>
        </div>
        <span
          className={cn(
            'shrink-0 rounded-full px-2 py-0.5 text-xs font-medium',
            trip.status === 'home' ? 'bg-muted text-muted-foreground' : 'bg-primary/10 text-primary'
          )}
        >
          {trip.status === 'home' ? t('traveling.home') : t('traveling.traveling')}
        </span>
      </div>

      {/* Route */}
      <ol className="flex flex-wrap items-center gap-1" aria-label={t('traveling.route')}>
        {route.map((stop, i) => (
          <li key={stop.key} className="flex items-center gap-1">
            {i > 0 && (
              <span className="text-xs text-muted-foreground" aria-hidden="true">
                →
              </span>
            )}
            <span
              className={cn(
                'rounded-full ring-2',
                stop.state === 'current'
                  ? 'ring-primary'
                  : stop.state === 'done'
                    ? 'ring-success/60'
                    : 'opacity-40 ring-transparent'
              )}
              title={name(stop.userId)}
            >
              <MemberAvatar
                userId={stop.userId ?? stop.key}
                name={name(stop.userId)}
                avatarUrl={stop.userId ? members.get(stop.userId)?.avatar_url : null}
                size="sm"
              />
            </span>
          </li>
        ))}
      </ol>

      <p className="text-sm text-foreground">
        {trip.status === 'home'
          ? t('traveling.backHome', { date: trip.finished_on ? shortDate(trip.finished_on) : '' })
          : current
            ? `📍 ${t(daysWithReader === 0 ? 'traveling.withReaderToday' : 'traveling.withReader', {
                name: current.user_id === user?.id ? t('clubs.members.you') : name(current.user_id),
                count: daysWithReader,
              })}`
            : ''}
        {trip.status === 'traveling' && (
          <span className="text-muted-foreground">
            {' · '}
            {next ? t('traveling.next', { name: name(next.user_id) }) : t('traveling.nextHome')}
          </span>
        )}
      </p>

      {(trip.note || notes.length > 0) && (
        <ul className="space-y-1.5 rounded-md bg-muted/40 p-3 text-sm">
          {trip.note && (
            <li className="break-words">
              <span className="font-medium text-foreground">{owner?.display_name}:</span>{' '}
              <span className="text-muted-foreground">{trip.note}</span>
            </li>
          )}
          {notes.map((s) => (
            <li key={s.id} className="break-words">
              <span className="font-medium text-foreground">{name(s.user_id)}:</span>{' '}
              <span className="text-muted-foreground">{s.note}</span>
            </li>
          ))}
        </ul>
      )}

      {canPass &&
        (passing ? (
          <form onSubmit={submit} className="space-y-2">
            <textarea
              value={note}
              maxLength={1000}
              rows={2}
              onChange={(e) => setNote(e.target.value)}
              placeholder={t('traveling.passNotePlaceholder')}
              aria-label={t('traveling.passNotePlaceholder')}
              className="w-full rounded-lg border border-input bg-card px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
            />
            <div className="flex gap-2">
              <Button type="submit" size="sm" disabled={pass.isPending}>
                {next
                  ? t('traveling.passTo', { name: name(next.user_id) })
                  : t('traveling.sendHome')}
              </Button>
              <Button type="button" size="sm" variant="ghost" onClick={() => setPassing(false)}>
                {t('common.cancel')}
              </Button>
            </div>
          </form>
        ) : (
          <Button size="sm" onClick={() => setPassing(true)}>
            {t('traveling.pass')}
          </Button>
        ))}
    </li>
  )
}
